import cx from 'classnames'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import { QuizContext } from '@f/applications/QuizApp/context/QuizContext'
import {
  composeTileOptions,
  RadioStepOption
} from '@f/applications/QuizApp/partials/composeTileOptions'
import { QuestionRadioButton } from '@f/applications/QuizApp/questions/QuestionRadio/QuestionRadioButton'

import { $TSFixMe } from '@f-fertighaus/applications/UserAccount/interfaces/utils'

import QuestionHeading from '../partials/QuestionHeading'
import { QuizStep } from '../partials/types'

interface Props {
  step: QuizStep
}

export type ConstructionMethodValue =
  | 'na'
  | 'all'
  | 'prefab'
  | 'blockhouse'
  | 'solid'
  | 'no-additional'
  | ''

export const CONSTRUCTION_METHOD_SUBSTEP_HEADING =
  'Welche weitere Bauweise kommt für Sie in Frage?'

export const QuestionConstructionMethod = ({ step }: Props) => {
  const {
    actions: { updateValues, move },
    tracking,
    tileStyle
  } = useContext(QuizContext) as $TSFixMe

  const baseConstructionMethodOptions = step.options?.slice(
    0,
    4
  ) as RadioStepOption[]

  const allConstructionMethodOption = step.options?.[4] as RadioStepOption
  const noMoreConstructionMethodOption = step.options?.[5] as RadioStepOption

  const [showConstructionMethod, setShowConstructionMethod] = useState<
    'base' | 'additional'
  >('base')
  const [
    selectedConstructionMethod,
    setSelectedConstructionMethod
  ] = useState<ConstructionMethodValue>('')
  const [containerHeight, setContainerHeight] = useState(0)

  const composeTiles = composeTileOptions(tileStyle)

  useEffect(() => {
    const activeStep = document.querySelector(
      '.quiz-construction-method__first-step'
    ) as HTMLElement

    if (activeStep) {
      setContainerHeight(activeStep.offsetHeight)
    }
  }, [])

  useEffect(() => {
    if (showConstructionMethod === 'additional') {
      window._wt({
        category: 'question',
        action: 'display',
        values: [
          {
            type: 'name',
            value: 'construction-method-additional'
          }
        ],
        ...tracking.flow
      })
    }
  }, [showConstructionMethod, tracking.flow])

  const submitConstructionMethodValue = (value: ConstructionMethodValue[]) => {
    updateValues({
      stepID: step.id,
      valuesToSave: [
        {
          name: 'construction_method',
          value: value
        }
      ]
    })
    move({ to: 'next', triggerSubmit: true })
  }

  const handleConstructionMethodChange = (value: ConstructionMethodValue) => {
    if (value === 'all') {
      submitConstructionMethodValue(['solid', 'prefab', 'blockhouse'])
      return
    }
    if (value === 'na') {
      submitConstructionMethodValue(['na'])
      return
    }
    if (value === 'no-additional') {
      submitConstructionMethodValue([selectedConstructionMethod])
      return
    }

    if (selectedConstructionMethod.length > 0) {
      submitConstructionMethodValue([value, selectedConstructionMethod])
    } else {
      setSelectedConstructionMethod(value)
      setShowConstructionMethod('additional')
    }
  }

  const constructionMethodOptionsWithoutSelected = baseConstructionMethodOptions.filter(
    constructionMethodOption =>
      constructionMethodOption.value !== selectedConstructionMethod &&
      constructionMethodOption.value !== 'na'
  )

  const currentRenderOptions = useMemo(() => {
    const getConstructionMethodOptions = () => {
      return {
        base: composeTiles(baseConstructionMethodOptions),
        additional: composeTiles([
          ...constructionMethodOptionsWithoutSelected,
          allConstructionMethodOption,
          noMoreConstructionMethodOption
        ]) //6 steps
      }[showConstructionMethod]
    }

    return getConstructionMethodOptions()
  }, [showConstructionMethod, constructionMethodOptionsWithoutSelected])

  const composeTrackingLocation = (wtStepName: string) => {
    return {
      base: `${wtStepName}`,
      additional: 'construction-method-additional'
    }[showConstructionMethod]
  }

  const questionContent = () => {
    return (
      <div
        className={cx(
          '[ f-m-h-auto ]',
          'QuestionRadio-wrap',
          currentRenderOptions.length > 4 && 'has-more-than-four-elements',
          currentRenderOptions.length === 4 && 'has-four-elements'
        )}>
        <div className='[ f-d-flc ] QuestionRadio-options'>
          <div
            className={cx(
              tileStyle === 'card'
                ? 'QuestionRadio-card-tiles'
                : 'QuestionRadio-bar-tiles'
            )}>
            {currentRenderOptions.map(({ ItemTemplate, ...option }) => (
              <QuestionRadioButton
                key={option.value}
                wtData={{
                  location: `question-${composeTrackingLocation(step._wtName)}`,
                  ...tracking.flow
                }}
                onClick={
                  option.onClick
                    ? option.onClick
                    : () =>
                        handleConstructionMethodChange(
                          option.value as ConstructionMethodValue
                        )
                }>
                <ItemTemplate {...option} />
              </QuestionRadioButton>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className='quiz-construction-method__container'
      style={{ height: `${containerHeight}px` }}>
      <div
        className={cx(
          'quiz-construction-method__first-step',
          selectedConstructionMethod !== '' &&
            'quiz-construction-method__first-step--out'
        )}>
        <QuestionHeading heading={step.heading} subheading={step.subheading} />
        {questionContent()}
      </div>

      <div
        className={cx(
          'quiz-construction-method__second-step',
          selectedConstructionMethod !== ''
            ? 'quiz-construction-method__second-step--in'
            : 'f-hidden'
        )}>
        <QuestionHeading heading={CONSTRUCTION_METHOD_SUBSTEP_HEADING} />
        {questionContent()}
      </div>
    </div>
  )
}
